import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faLocationDot } from "@fortawesome/pro-solid-svg-icons/faLocationDot";
import { faGlobe } from "@fortawesome/pro-solid-svg-icons/faGlobe";
import { faExpand } from "@fortawesome/pro-solid-svg-icons/faExpand";
import { faHandHoldingHeart } from "@fortawesome/pro-solid-svg-icons/faHandHoldingHeart";
import { cn } from "@wondermaps/ui";
import type { SponsorTierGroup } from "@wondermaps/db";
import type { SponsorLogo } from "./sponsor-types";
import { VisitorDrawer } from "./VisitorDrawer";
import { SheetHeader } from "./SheetHeader";

interface SponsorsBottomSheetProps {
  open: boolean;
  onClose: () => void;
  sponsors: SponsorTierGroup[];
  onSponsorClick?: (sponsor: SponsorLogo, placement: string) => void;
  /** Resolve POI name from ID (from visitor POI list) */
  getPoiName?: (poiId: string) => string | undefined;
  /** Navigate to a specific POI */
  onPoiClick?: (poiId: string) => void;
  /** Called when user taps expand to full-screen */
  onExpand?: () => void;
}

export function SponsorsBottomSheet({
  open,
  onClose,
  sponsors,
  onSponsorClick,
  getPoiName,
  onPoiClick,
  onExpand,
}: SponsorsBottomSheetProps) {
  const { t } = useTranslation("map");

  return (
    <VisitorDrawer
      open={open}
      onOpenChange={(o) => !o && onClose()}
      maxHeightClass="max-h-[85vh]"
      title={t("menu.ourSponsors")}
    >
      <SheetHeader
        title={t("menu.ourSponsors")}
        icon={<FontAwesomeIcon icon={faHandHoldingHeart} className="h-5 w-5 text-muted-foreground" />}
        onClose={onClose}
        closeLabel={t("sponsors.close")}
        action={
          onExpand ? (
            <button
              type="button"
              onClick={onExpand}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={t("sponsors.expand")}
            >
              <FontAwesomeIcon icon={faExpand} className="h-4 w-4 text-muted-foreground" />
            </button>
          ) : undefined
        }
      />

      <div className="flex-1 overflow-y-auto">
        <SponsorsBody
          sponsors={sponsors}
          onSponsorClick={onSponsorClick}
          getPoiName={getPoiName}
          onPoiClick={onPoiClick}
        />
      </div>
    </VisitorDrawer>
  );
}

/* ── Shared body (used by both sheet and full-screen) ── */

export interface SponsorsBodyProps {
  sponsors: SponsorTierGroup[];
  onSponsorClick?: (sponsor: SponsorLogo, placement: string) => void;
  getPoiName?: (poiId: string) => string | undefined;
  onPoiClick?: (poiId: string) => void;
  /** Analytics placement identifier */
  placement?: string;
}

export function SponsorsBody({
  sponsors,
  onSponsorClick,
  getPoiName,
  onPoiClick,
  placement = "sponsors_sheet",
}: SponsorsBodyProps) {
  return (
    <div className="pb-safe p-4">
      {sponsors.map((tier) => {
        if (tier.sponsors.length === 0) return null;
        return (
          <div key={tier.slug} className="mb-4">
            {/* Tier header */}
            <div className="flex items-center gap-2 mb-2">
              {tier.color && (
                <span
                  className="h-2.5 w-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: tier.color }}
                />
              )}
              <h3 className="text-xs font-semibold text-muted-foreground uppercase">
                {tier.name}
              </h3>
            </div>

            {/* Sponsor rows */}
            <div className="space-y-1">
              {tier.sponsors.map((sponsor) => (
                <SponsorRow
                  key={sponsor.id}
                  sponsor={sponsor}
                  onSponsorClick={onSponsorClick}
                  getPoiName={getPoiName}
                  onPoiClick={onPoiClick}
                  placement={placement}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Sponsor row ── */

function SponsorRow({
  sponsor,
  onSponsorClick,
  getPoiName,
  onPoiClick,
  placement = "sponsors_sheet",
}: {
  sponsor: SponsorLogo;
  onSponsorClick?: (sponsor: SponsorLogo, placement: string) => void;
  getPoiName?: (poiId: string) => string | undefined;
  onPoiClick?: (poiId: string) => void;
  placement?: string;
}) {
  const { t } = useTranslation("map");
  const [expanded, setExpanded] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const poiIds = sponsor.linkedPoiIds ?? [];
  const hasMultiplePois = poiIds.length > 1;

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center gap-3 p-3">
        {/* Logo */}
        <div className="h-10 w-16 shrink-0 flex items-center justify-center">
          {sponsor.logoUrl && !imgFailed ? (
            <img
              src={sponsor.logoUrl}
              alt={sponsor.name || "Sponsor"}
              className="max-h-10 max-w-16 object-contain"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <span className="text-xs text-muted-foreground truncate">
              {sponsor.name}
            </span>
          )}
        </div>

        {/* Name + website */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{sponsor.name}</p>
          {sponsor.websiteUrl && (
            <a
              href={sponsor.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onSponsorClick?.(sponsor, placement)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faGlobe} className="h-3 w-3" />
              <span className="truncate">
                {sponsor.websiteUrl
                  .replace(/^https?:\/\/(www\.)?/, "")
                  .replace(/\/$/, "")}
              </span>
            </a>
          )}
        </div>

        {/* POI action */}
        {poiIds.length === 1 && onPoiClick && (
          <button
            type="button"
            onClick={() => onPoiClick(poiIds[0])}
            className="shrink-0 flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors px-2 py-1 rounded-md hover:bg-muted/50 min-h-[44px]"
          >
            <FontAwesomeIcon icon={faLocationDot} className="h-3 w-3" />
            <span>{t("sponsors.viewPoi")}</span>
          </button>
        )}
        {hasMultiplePois && onPoiClick && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="shrink-0 flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors px-2 py-1 rounded-md hover:bg-muted/50 min-h-[44px]"
          >
            <FontAwesomeIcon icon={faLocationDot} className="h-3 w-3" />
            <span>{poiIds.length}</span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={cn(
                "h-2.5 w-2.5 transition-transform",
                expanded && "rotate-180",
              )}
            />
          </button>
        )}
      </div>

      {/* Expandable POI list */}
      {expanded && hasMultiplePois && onPoiClick && (
        <div className="border-t px-3 py-2 space-y-1">
          {poiIds.map((poiId) => {
            const name = getPoiName?.(poiId) ?? poiId;
            return (
              <button
                key={poiId}
                type="button"
                onClick={() => onPoiClick(poiId)}
                className="flex items-center gap-2 w-full text-left text-xs text-muted-foreground hover:text-foreground py-1.5 px-2 rounded hover:bg-muted/50 transition-colors min-h-[36px]"
              >
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="h-3 w-3 shrink-0"
                />
                <span className="truncate">{name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
